import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  FormControl, 
  Select, 
  MenuItem, 
  TextField, 
  Slider, 
  useTheme,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronRight } from 'lucide-react';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Estilo para diálogos estilo Apple
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    overflow: 'hidden'
  }
}));

// Estilo para botones en diálogos
const DialogButton = styled(Box)(({ theme, primary }) => ({
  borderRadius: 12,
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(primary && {
    background: 'linear-gradient(135deg, #FF9800 0%, #FF6B00 100%)',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#E65100',
      boxShadow: '0 4px 12px rgba(255, 107, 0, 0.3)',
    },
  }),
  ...(!primary && {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
    },
  }),
}));

// Estilo para inputs en diálogos
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.text
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  },
}));

// Estilo para selects en diálogos
const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.text
      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: FIT_ORANGE,
  },
}));

// Componente para animar los números de calorías
const AnimatedNumber = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValue = useRef(value);

  useEffect(() => {
    // Si el valor ha cambiado
    if (value !== previousValue.current) {
      const start = previousValue.current;
      const end = value;
      const duration = 1000; // 1 segundo de duración
      const startTime = Date.now();

      const animateValue = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        
        if (elapsed < duration) {
          // Calcular el valor actual basado en el tiempo transcurrido
          const progress = elapsed / duration;
          const current = Math.round(start + (end - start) * progress);
          setDisplayValue(current);
          requestAnimationFrame(animateValue);
        } else {
          // Asegurarse de que termine exactamente en el valor final
          setDisplayValue(end);
        }
      };
      
      requestAnimationFrame(animateValue);
      previousValue.current = value;
    }
  }, [value]);

  // Convertir el número a string y separar sus dígitos
  const digits = displayValue.toString().split('');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
      {digits.map((digit, index) => (
        <Typography 
          key={index}
          variant="h3" 
          component="span" 
          fontWeight={600}
          sx={{
            display: 'inline-block',
            minWidth: '0.6em',
            textAlign: 'center',
            background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {digit}
        </Typography>
      ))}
    </Box>
  );
};

const Goals = ({ goalData, loading, onUpdateGoal }) => {
  const theme = useTheme();
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalType, setGoalType] = useState(goalData?.goalType || 'loss');
  const [goalWeight, setGoalWeight] = useState(goalData?.goalWeight?.toString() || '70');
  const [goalSpeed, setGoalSpeed] = useState(goalData?.goalSpeed || 5);
  const [baseCalories, setBaseCalories] = useState(0);
  const [adjustedCalories, setAdjustedCalories] = useState(0);
  const [weightError, setWeightError] = useState('');
  
  // Función para calcular calorías base según los datos físicos del usuario
  const calculateBaseCalories = (userData) => {
    if (!userData) return 2000;
    
    // Extraer datos relevantes
    const { sex, age, weight, height, activityLevel } = userData;
    
    // Valores por defecto si no hay datos
    const userSex = sex || 'male';
    const userAge = age || 30;
    const userWeight = weight || 70; // Usar el campo weight
    const userHeight = height || 170;
    const userActivity = activityLevel || 'sedentary';
    
    // Fórmula Harris-Benedict para calcular BMR (metabolismo basal)
    let bmr = 0;
    if (userSex === 'male') {
      bmr = 88.362 + (13.397 * userWeight) + (4.799 * userHeight) - (5.677 * userAge);
    } else {
      bmr = 447.593 + (9.247 * userWeight) + (3.098 * userHeight) - (4.330 * userAge);
    }
    
    // Multiplicador según nivel de actividad
    let activityMultiplier = 1.2; // Sedentario
    if (userActivity === 'lightly_active') {
      activityMultiplier = 1.375;
    } else if (userActivity === 'moderately_active') {
      activityMultiplier = 1.55;
    } else if (userActivity === 'very_active') {
      activityMultiplier = 1.725;
    } else if (userActivity === 'extra_active') {
      activityMultiplier = 1.9;
    }
    
    // Calorías para mantenimiento
    const maintenanceCalories = Math.round(bmr * activityMultiplier);
    
    return maintenanceCalories;
  };
  
  // Función para aplicar ajuste según tipo de objetivo y velocidad
  const applyGoalAdjustment = (baseCals, type, speed) => {
    let adjusted = baseCals;
    
    // Ajustar calorías según tipo de objetivo y velocidad
    if (type === 'loss') {
      // Para pérdida de peso: reducir calorías (más velocidad = menos calorías)
      adjusted = Math.round(baseCals - (speed * 50));
    } else if (type === 'gain') {
      // Para ganancia de peso: aumentar calorías (más velocidad = más calorías)
      adjusted = Math.round(baseCals + (speed * 50));
    }
    // Para mantenimiento, mantener las calorías base
    
    return adjusted;
  };

  // Validar el peso objetivo según el tipo de objetivo
  const validateGoalWeight = (weight, type, currentWeight) => {
    const goalWeightNum = parseFloat(weight);
    const currentWeightNum = parseFloat(currentWeight);
    
    if (isNaN(goalWeightNum)) {
      return "Please enter a valid number";
    }
    
    if (type === 'loss' && goalWeightNum >= currentWeightNum) {
      return "Goal weight must be less than current weight for weight loss";
    }
    
    if (type === 'gain' && goalWeightNum <= currentWeightNum) {
      return "Goal weight must be greater than current weight for weight gain";
    }
    
    return "";
  };

  // Efecto para calcular calorías base cuando se abre el modal
  useEffect(() => {
    if (goalData && goalDialogOpen) {
      // Calcular calorías base a partir del peso y otros datos físicos
      const baseCals = calculateBaseCalories(goalData);
      setBaseCalories(baseCals);
      
      // Calcular el valor ajustado según el tipo y velocidad actuales
      const adjusted = applyGoalAdjustment(baseCals, goalType, goalSpeed);
      setAdjustedCalories(adjusted);
      
      // Validar el peso objetivo inicial
      if (goalType !== 'maintenance') {
        const error = validateGoalWeight(goalWeight, goalType, goalData.currentWeight);
        setWeightError(error);
      } else {
        setWeightError('');
      }
    }
  }, [goalData, goalDialogOpen, goalType, goalSpeed, goalWeight]);

  // Efecto para recalcular calorías cuando cambia el tipo o velocidad
  useEffect(() => {
    if (baseCalories > 0) {
      const adjusted = applyGoalAdjustment(baseCalories, goalType, goalSpeed);
      setAdjustedCalories(adjusted);
    }
    
    // Si cambia el tipo de objetivo, validar el peso objetivo
    if (goalData) {
      if (goalType === 'maintenance') {
        // En caso de mantenimiento, establecer el peso objetivo cercano al actual
        setGoalWeight(goalData.currentWeight.toString());
        setWeightError('');
      } else {
        const error = validateGoalWeight(goalWeight, goalType, goalData.currentWeight);
        setWeightError(error);
      }
    }
  }, [baseCalories, goalType, goalSpeed, goalData, goalWeight]);

  // Efecto para validar el peso objetivo cuando cambia
  useEffect(() => {
    if (goalData && goalType !== 'maintenance') {
      const error = validateGoalWeight(goalWeight, goalType, goalData.currentWeight);
      setWeightError(error);
    }
  }, [goalWeight, goalType, goalData]);

  const handleOpenGoalDialog = () => {
    // Actualiza los valores con los datos actuales del objetivo
    if (goalData) {
      setGoalType(goalData.goalType || 'loss');
      setGoalWeight(goalData.goalWeight?.toString() || '70');
      setGoalSpeed(goalData.goalSpeed || 5);
      
      // Calcular calorías base a partir del peso y otros datos físicos
      const baseCals = calculateBaseCalories(goalData);
      setBaseCalories(baseCals);
      
      // Aplicar ajuste según el tipo y velocidad actuales
      const adjusted = applyGoalAdjustment(baseCals, goalData.goalType || 'loss', goalData.goalSpeed || 5);
      setAdjustedCalories(adjusted);
    }
    setGoalDialogOpen(true);
  };

  const handleCloseGoalDialog = () => {
    setGoalDialogOpen(false);
  };

  const handleGoalTypeChange = (newType) => {
    setGoalType(newType);
    
    // Si cambia a mantenimiento, ajustar el peso objetivo
    if (newType === 'maintenance' && goalData) {
      setGoalWeight(goalData.currentWeight.toString());
    }
  };

  const handleGoalSpeedChange = (newSpeed) => {
    setGoalSpeed(newSpeed);
  };

  const handleSaveGoal = () => {
    // Solo guardar si no hay error de peso
    if (!weightError) {
      const goalDataToSave = {
        goalWeight: parseFloat(goalWeight),
        goalType,
        goalSpeed,
        goalAutoAdjust: true, // Siempre es true ya que eliminamos el switch
        goalCalories: adjustedCalories,
        calculatedCalories: adjustedCalories,
        baseCalories: baseCalories
      };
      
      onUpdateGoal(goalDataToSave);
      handleCloseGoalDialog();
    }
  };

  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Goal
        </Typography>
        <IconButton onClick={handleOpenGoalDialog} sx={{ 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)',
        }}>
          <ChevronRight size={20} />
        </IconButton>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
        </Box>
      ) : goalData ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Days left to achieve goal: <strong>{goalData.daysRemaining || 0} days</strong>
          </Typography>
          
          {/* Barra de progreso */}
          <Box sx={{ mb: 4, position: 'relative' }}>
            <Box sx={{ 
              position: 'relative', 
              height: 14, 
              borderRadius: 7, 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              mb: 1
            }}>
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${goalData.progressPercentage || 0}%`,
                  borderRadius: 7,
                  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
                }}
              />
            </Box>
            
            {/* Pesos actual y objetivo */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Current: {goalData.currentWeight} kg
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                Goal: {goalData.goalWeight} kg
              </Typography>
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No goal set. Click the button to create one.
          </Typography>
        </Box>
      )}

      {/* Diálogo para editar goal */}
      <StyledDialog 
        open={goalDialogOpen} 
        onClose={handleCloseGoalDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}` }}>
          <Typography variant="h5" fontWeight={600}>
            Goal & Calories
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Goal
            </Typography>
            <FormControl fullWidth>
              <StyledSelect
                value={goalType}
                onChange={(e) => handleGoalTypeChange(e.target.value)}
              >
                <MenuItem value="loss">Weight Loss</MenuItem>
                <MenuItem value="gain">Weight Gain</MenuItem>
                <MenuItem value="maintenance">Weight Maintenance</MenuItem>
              </StyledSelect>
            </FormControl>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Goal Weight
            </Typography>
            {goalType === 'maintenance' && goalData ? (
              // Para mantenimiento, mostrar un selector con opciones de ±1kg
              <FormControl fullWidth>
                <StyledSelect
                  value={goalWeight}
                  onChange={(e) => setGoalWeight(e.target.value)}
                >
                  <MenuItem value={(goalData.currentWeight - 1).toString()}>
                    {goalData.currentWeight - 1} kg
                  </MenuItem>
                  <MenuItem value={goalData.currentWeight.toString()}>
                    {goalData.currentWeight} kg (current)
                  </MenuItem>
                  <MenuItem value={(goalData.currentWeight + 1).toString()}>
                    {goalData.currentWeight + 1} kg
                  </MenuItem>
                </StyledSelect>
              </FormControl>
            ) : (
              // Para pérdida o ganancia, mostrar campo de texto con validación
              <StyledTextField
                fullWidth
                value={goalWeight}
                onChange={(e) => setGoalWeight(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                }}
                error={!!weightError}
                helperText={weightError}
              />
            )}
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Speed
            </Typography>
            <Slider
              value={goalSpeed}
              onChange={(e, newValue) => handleGoalSpeedChange(newValue)}
              min={1}
              max={10}
              marks={[
                { value: 1, label: 'Slow' },
                { value: 10, label: 'Fast' }
              ]}
              sx={{
                '& .MuiSlider-thumb': {
                  color: FIT_ORANGE,
                },
                '& .MuiSlider-track': {
                  color: FIT_ORANGE,
                },
                '& .MuiSlider-rail': {
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                }
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
              {goalSpeed <= 3 ? '16 weeks' : goalSpeed <= 6 ? '10 weeks' : '6 weeks'}
            </Typography>
          </Box>
          
          {/* Sección de calorías rediseñada */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Calories
            </Typography>
            
            {/* Componente de número animado */}
            <AnimatedNumber value={adjustedCalories} />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Based on your goal and activity level
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <DialogButton 
            variant="contained"
            fullWidth
            onClick={handleSaveGoal}
            primary={true}
            sx={{ py: 1.5 }}
            disabled={!!weightError}
          >
            Save
          </DialogButton>
        </DialogActions>
      </StyledDialog>
    </Box>
  );
};

export default Goals;